<template>
  <v-sheet elevation="0" class="ma-2 px-2 h85" v-if="scheduler">
    <v-row>
      <v-col>
        <BackBtn to="/scheduler" />
      </v-col>
    </v-row>
    <Title
      :title="
        scheduler.name ? scheduler.name : 'Insert a new Scheduler title...'
      "
      :icon="scheduler.name ? 'clipboard-history' : 'cancel'"
      editable
    />
    <v-card class="ma-1 pa-1" elevation="4">
      <v-row class="ma-1">
        <v-col class="ma-1">
          <v-row class="mt-1">
            <v-col>
              <h4><v-icon>mdi-cog-outline</v-icon> General:</h4>
            </v-col>
          </v-row>
          <v-row class="my-1">
            <v-col>
              <v-text-field
                v-model="scheduler.name"
                label="Scheduler Name"
                :rules="rules.text"
                required
                dense
              >
              </v-text-field>
            </v-col>
            <v-divider vertical> </v-divider>
            <v-col>
              <v-row class="mt-1">
                <v-col cols="auto">
                  <v-switch class="my-0" v-model="scheduler.active"> </v-switch>
                </v-col>
                <v-col cols="auto">
                  <h4 class="my-1"><v-icon>mdi-power</v-icon> Active</h4>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider> </v-divider>
          <v-row class="mt-1">
            <v-col>
              <h4><v-icon>mdi-calendar-clock</v-icon> Time Frame:</h4>
            </v-col>
          </v-row>
          <v-row class="my-1">
            <v-col>
              <Cron :cron="scheduler.cron" @changed="updateCron" />
            </v-col>
          </v-row>
        </v-col>
        <v-divider vertical> </v-divider>
        <v-col class="ma-1">
          <v-row class="mt-1">
            <v-col>
              <h4>
                <v-icon
                  >mdi-{{
                    suitesToggle ? "package-variant" : "flask-outline"
                  }}</v-icon
                >
                {{ suitesToggle ? "Test Suites" : "Plans" }}:
              </h4>
            </v-col>
          </v-row>
          <section v-if="scheduler.plans">
            <v-row>
              <v-col>
                <v-combobox
                  :items="items"
                  v-model="scheduler.plans"
                  :label="
                    'Search & Select ' + (suitesToggle ? 'Test Suite' : 'Plan')
                  "
                  outlined
                  clearable
                  multiple
                  dense
                >
                </v-combobox>
              </v-col>
            </v-row>
            <DragList :list="scheduler.plans" @changed="updateList">
              <template v-slot:title>
                <v-icon>mdi-order-bool-ascending-variant</v-icon> Plans Order:
              </template>
            </DragList>
          </section>
        </v-col>
      </v-row>
    </v-card>
    <v-row>
      <v-spacer> </v-spacer>
      <v-col cols="auto">
        <v-btn
          class="ma-3"
          :disabled="!valid"
          label="Save"
          color="success"
          @click="save"
        >
          <v-icon dark> mdi-content-save </v-icon> Save
        </v-btn>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";

import BackBtn from "../components/buttons/BackBtn.vue";
import Cron from "../components/Cron.vue";
import Title from "../components/Title.vue";
import DragList from "../components/DragList.vue";
import { Scheduler, SelectItem } from "@/types";

export default Vue.extend({
  name: "SchedulerView",
  components: {
    BackBtn,
    Title,
    Cron,
    DragList,
  },
  data() {
    return {
      rules: {
        text: [
          (val: string) => (val && val.length) || "This field is required",
        ],
      },
      suitesToggle: false,
    };
  },
  computed: {
    valid(): boolean {
      return (
        this.scheduler?.name?.length > 0 &&
        this.scheduler?.cron?.length > 0 &&
        this.scheduler?.plans?.length > 0
      );
    },
    scheduler: {
      get(): Scheduler {
        return this.$store.getters["Scheduler/getCurrentSchedule"];
      },
      set(scheduler: Scheduler) {
        this.$store.commit("Scheduler/setCurrentSchedule", scheduler);
      },
    },
    items(): SelectItem[] {
      // TODO Suites support
      const funcText = this.suitesToggle
        ? "Suites/getSuites"
        : "Plans/getPlans";
      const items = this.$store.getters[funcText];

      return items.map((item: { name: string; _id: string }) => {
        return {
          text: item.name,
          value: item._id,
          icon: this.suitesToggle ? "package-variant" : "flask",
        };
      });
    },
  },
  methods: {
    removePlan(index: number) {
      this.scheduler.plans.splice(index, 1);
    },
    updateList(list: SelectItem) {
      this.scheduler.plans = list;
    },
    updateCron(obj: { cron: string; timePhrase: string }) {
      const { cron, timePhrase } = obj;
      this.scheduler.cron = cron;
      this.scheduler.timePhrase = timePhrase;
    },
    schedulerChanged() {
      this.$store.commit("Scheduler/setCurrentSchedule", this.scheduler);
    },
    save() {
      // modify plans list to string[]
      const plans = this.scheduler.plans.map((s: SelectItem) => s.value);
      this.scheduler.plans = plans;
      this.$store.dispatch("Scheduler/saveSchedule", this.scheduler);
      this.$store.commit("Scheduler/setCurrentSchedule");
      this.$router.push("/scheduler");
    },
  },

  created() {
    this.$store.commit("setGlobalLoader", true);
    // TODO filter
    // const filter = {};
    this.$store.commit("Scheduler/setCurrentSchedule");
    this.$store.dispatch("Plans/fetchAllPlans");
    this.$store.dispatch("Suites/fetchSuites");
    const { id } = this.$route.params;
    if (id) {
      this.$store.dispatch("Scheduler/fetchCurrentSchedule", id);
    }
  },
  destroyed() {
    this.$store.commit("Scheduler/setCurrentSchedule");
  },
});
</script>
